// REST client for the API server (server/app.ts). Session is an httpOnly cookie; the custom header is the CSRF guard.
import { ApiError, type Api } from "../api";

async function call<T>(base: string, method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      credentials: "same-origin",
      headers: { "X-Requested-With": "order-ai", ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("SERVER", "Cannot reach the server. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const code = (data as { code?: ApiError["code"] } | null)?.code ?? (res.status === 401 ? "UNAUTHENTICATED" : res.status === 403 ? "FORBIDDEN" : res.status === 404 ? "NOT_FOUND" : res.status === 409 ? "CONFLICT" : "SERVER");
    throw new ApiError(code, (data as { message?: string } | null)?.message ?? `Server error (${res.status})`, (data as { detail?: unknown } | null)?.detail);
  }
  return data as T;
}

export function createHttpApi(base: string): Api {
  const get = <T>(p: string) => call<T>(base, "GET", p);
  const post = <T>(p: string, b?: unknown) => call<T>(base, "POST", p, b ?? {});
  const put = <T>(p: string, b: unknown) => call<T>(base, "PUT", p, b);
  const patch = <T>(p: string, b: unknown) => call<T>(base, "PATCH", p, b);
  const del = <T>(p: string) => call<T>(base, "DELETE", p);
  const enc = encodeURIComponent;
  return {
    auth: {
      config: () => get("/auth/config"),
      google: (credential) => post("/auth/google", { credential }),
      dev: (email) => post("/auth/dev", { email }),
      password: (email, password) => post("/auth/password", { email, password }),
      logout: () => post("/auth/logout"),
      me: () => get("/auth/me"),
      signupResend: (email, ticket) => post("/auth/signup/resend", { email, ticket }),
      signupEdit: (email, ticket) => post("/auth/signup/edit", { email, ticket }),
    },
    users: {
      list: () => get("/users"),
      directory: () => get("/users/directory"),
      syncStatus: () => get("/users/sync"),
      sync: () => post("/users/sync"),
      rename: (email, names) => put(`/users/${encodeURIComponent(email)}/name`, names),
    },
    registrations: {
      channels: () => get("/registrations/channels"),
      approve: (email) => post(`/registrations/${encodeURIComponent(email)}/approve`),
      createTest: () => post("/dev/test-signups"),
      deleteTest: (email) => del(`/dev/test-signups/${encodeURIComponent(email)}`),
      reject: (email) => post(`/registrations/${encodeURIComponent(email)}/reject`),
      pending: () => get("/registrations/pending"),
    },
    team: {
      addPerson: (input) => post("/team/people", input),
      addPosition: (code, input) => post(`/team/people/${encodeURIComponent(code)}/positions`, input),
      changePosition: (key, input) => patch(`/team/positions/${encodeURIComponent(key)}`, input),
      removePosition: (key) => del(`/team/positions/${encodeURIComponent(key)}`),
      removePerson: (code) => del(`/team/people/${encodeURIComponent(code)}`),
      setPassword: (code, password) => put(`/team/people/${encodeURIComponent(code)}/password`, { password }),
      setStatus: (code, input) => post(`/team/people/${encodeURIComponent(code)}/status`, input),
      fixRow: (rowId, input) => post(`/team/rows/${encodeURIComponent(rowId)}/fix`, input),
      ignoreRow: (rowId) => post(`/team/rows/${encodeURIComponent(rowId)}/ignore`, {}),
      mirror: () => get("/team/mirror"),
    },
    master: { get: () => get("/master-data"), update: (md) => put("/master-data", md), history: () => get("/master-data/history") },
    orders: {
      list: () => get("/orders"),
      get: (code) => get(`/orders/${enc(code)}`),
      create: (i) => post("/orders", i),
      accept: (code) => post(`/orders/${enc(code)}/accept`),
      reject: (code, reason) => post(`/orders/${enc(code)}/reject`, { reason }),
      remove: (code) => del(`/orders/${enc(code)}`),
      deleted: () => get("/orders-deleted"),
      restore: (code) => post(`/orders/${enc(code)}/restore`, {}),
      setCharge: (code, status) => post(`/orders/${enc(code)}/charge`, { status }),
      setInfo: (code, status, note) => post(`/orders/${enc(code)}/info`, { status, note }),
      setReworkCharge: (code, on) => post(`/orders/${enc(code)}/rework-charge`, { on }),
      handover: (code, shift, note) => post(`/orders/${enc(code)}/handover`, { shift, note }),
    },
    effects: {
      claim: (o, e, i) => post(`/orders/${enc(o)}/effects/${enc(e)}/claim`, i),
      claimMany: (o, items) => post(`/orders/${enc(o)}/claim`, { items }),
      assign: (o, e, staffId, i) => post(`/orders/${enc(o)}/effects/${enc(e)}/assign`, { staffId, ...i }),
      submit: (o, e, i) => post(`/orders/${enc(o)}/effects/${enc(e)}/submissions`, i),
      setLevel: (o, e, levelId) => post(`/orders/${enc(o)}/effects/${enc(e)}/level`, { levelId }),
      qcDecide: (o, e, i) => post(`/orders/${enc(o)}/effects/${enc(e)}/qc`, i),
      qcPassMany: (o, items) => post(`/orders/${enc(o)}/qc-pass`, { items }),
    },
    chat: {
      threads: () => get("/chat/threads"),
      messages: (code) => get(`/chat/${enc(code)}/messages`),
      send: (code, text, attachmentIds) => post(`/chat/${enc(code)}/messages`, { text, attachmentIds }),
    },
    dm: {
      threads: () => get("/dm/threads"),
      messages: (peerId) => get(`/dm/${enc(peerId)}/messages`),
      send: (peerId, text, attachmentIds) => post(`/dm/${enc(peerId)}/messages`, { text, attachmentIds }),
    },
    presence: { list: () => get("/presence") },
    uploads: {
      upload: async (file) => {
        let res: Response;
        try {
          res = await fetch(`${base}/uploads`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "X-Requested-With": "order-ai", "Content-Type": file.type || "application/octet-stream", "X-File-Name": encodeURIComponent(file.name || "") },
            body: file,
          });
        } catch {
          throw new ApiError("SERVER", "Cannot reach the server. Check your connection and try again.");
        }
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new ApiError((data as { code?: ApiError["code"] } | null)?.code ?? "SERVER", (data as { message?: string } | null)?.message ?? `Upload failed (${res.status})`);
        return data as import("../api").Attachment;
      },
      url: (id) => `${base}/uploads/${enc(id)}`,
    },
    audit: { list: () => get("/audit-events"), recordDenied: (path) => post("/audit-events/access-denied", { path }) },
    notifications: { list: () => get("/notifications"), markAllRead: () => post("/notifications/read-all") },
    resources: {
      list: () => get("/resources"),
      save: (id, input) => (id ? put(`/resources/${enc(id)}`, input) : post("/resources", input)),
      remove: (id) => del(`/resources/${enc(id)}`),
    },
    feedback: {
      create: (i) => post("/feedback", i),
      list: () => get("/feedback"),
      update: (id, p) => patch(`/feedback/${enc(id)}`, p),
    },
  };
}
